import { useState, useEffect, useRef, useContext, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import leadsService from '../../services/leadsService';
import './Leads.css';

const blankLine  = () => ({ product_id: null, product_name: '', search: '', qty: 1, unit_price: '', total_price: 0, suggestions: [], showSuggestions: false, is_kit: false, kit_item_days: null, kit_schedules: [], kit_schedule_id: null, kit_consumption_days: null });
const blankLines = () => [blankLine(), blankLine(), blankLine()];
const DELIVERY_WINDOWS = ['07:00 - 09:00', '09:00 - 12:00', '12:00 - 16:00', '18:30 - 21:00'];
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SOURCE_CHANNELS = ['Meta Ads', 'WhatsApp', 'Referral', 'Walk-in', 'Social Media', 'Phone', 'App', 'Apple A Day', 'Promoter Partner'];
const INTERACTION_TYPES = ['Call', 'WhatsApp', 'Visit', 'Email', 'Follow-up'];
const OUTCOMES = ['Interested', 'Not Interested', 'Callback', 'No Answer'];
const STATUSES = ['New', 'Contacted', 'Interested', 'Converted', 'Order Placed', 'Ready for Subscription', 'Subscribed', 'Lost', 'Lost on Subscription'];

const NEXT_STATUS = {
  New: ['Contacted', 'Lost'],
  Contacted: ['Interested', 'Lost'],
  Interested: ['Lost'],
  Lost: ['Contacted'],
  'Order Placed': ['Ready for Subscription', 'Lost on Subscription'],
  'Ready for Subscription': ['Subscribed', 'Lost on Subscription'],
  'Lost on Subscription': ['Ready for Subscription'],
};

const STATUS_BADGE_CLASS = {
  New: 'status-new',
  Contacted: 'status-contacted',
  Interested: 'status-interested',
  Converted: 'status-converted',
  'Order Placed': 'status-order-placed',
  'Ready for Subscription': 'status-ready-sub',
  Subscribed: 'status-subscribed',
  Lost: 'status-lost',
  'Lost on Subscription': 'status-lost-sub',
};

const BLANK_LEAD_FORM = {
  name: '', phone: '', email: '', area: '',
  source_channel: 'Meta Ads', source_ref: '', notes: '',
};

const BLANK_INTERACTION_FORM = {
  interaction_type: '',
  direction: 'Outbound',
  channel: '',
  summary: '',
  outcome: '',
  next_action: '',
  next_action_date: '',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ORDER_STATE_BADGE = {
  Draft:     'status-new',
  Placed:    'status-order-placed',
  Delivered: 'status-converted',
  Cancelled: 'status-lost',
};

const Leads = () => {
  const { user } = useContext(AuthContext);

  const checkPermission = (name) => {
    if (!user?.permissions || !Array.isArray(user.permissions)) return false;
    return user.permissions.some(p =>
      (typeof p === 'object' && p.name === name) || p === name
    );
  };

  const hasCreatePermission  = checkPermission('leads.create');
  const hasUpdatePermission  = checkPermission('leads.update');
  const hasConvertPermission = checkPermission('leads.convert');
  const hasImportPermission  = checkPermission('leads.import');

  // ── List state ──────────────────────────────────────────
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: 'Active', source_channel: '', assigned_to: '', action_filter: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // ── Create / Edit modal ──────────────────────────────────
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadForm, setLeadForm] = useState(BLANK_LEAD_FORM);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // ── Detail modal ─────────────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLead, setDetailLead] = useState(null);
  const [detailInteractions, setDetailInteractions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Log interaction (inside detail modal) ────────────────
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [interactionForm, setInteractionForm] = useState(BLANK_INTERACTION_FORM);
  const [interactionSubmitting, setInteractionSubmitting] = useState(false);
  const [interactionError, setInteractionError] = useState('');

  // ── Status change (inside detail modal) ──────────────────
  const [statusNote, setStatusNote] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState('');

  // ── Resend Payment modal ──────────────────────────────────
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendDeliveryDate, setResendDeliveryDate] = useState('');
  const [resendDeliveryWindow, setResendDeliveryWindow] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendError, setResendError] = useState('');

  // ── Assign modal ─────────────────────────────────────────
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState('');

  // ── Convert modal ─────────────────────────────────────────
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertAddress, setConvertAddress] = useState('');
  const [convertDeliveryDate, setConvertDeliveryDate] = useState('');
  const [convertDeliveryWindow, setConvertDeliveryWindow] = useState('');
  const [convertLines, setConvertLines] = useState(blankLines());
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertTempPassword, setConvertTempPassword] = useState('');
  const [convertOrderNo, setConvertOrderNo] = useState('');
  const convertMsgRef = useRef(null);

  useEffect(() => {
    if (convertError || convertTempPassword) {
      convertMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [convertError, convertTempPassword]);

  // ── Fetch leads ──────────────────────────────────────────
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await leadsService.getLeads({
        q: searchTerm,
        status: filters.status,
        source_channel: filters.source_channel,
        assigned_to: filters.assigned_to,
        action_filter: filters.action_filter,
      });
      setLeads(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    leadsService.getUsersWithLeadsRead()
      .then(res => setAssignableUsers(res.data || []))
      .catch(() => {});
  }, []);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  // ── Create / Edit handlers ───────────────────────────────
  const handleCreateNew = () => {
    setEditingLead(null);
    setLeadForm(BLANK_LEAD_FORM);
    setFormError('');
    setShowFormModal(true);
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setLeadForm({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      area: lead.area || '',
      source_channel: lead.source_channel || '',
      source_ref: lead.source_ref || '',
      notes: lead.notes || '',
    });
    setFormError('');
    setShowFormModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setLeadForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      setFormError('Name and phone are required.');
      return;
    }
    setFormSubmitting(true);
    setFormError('');
    try {
      const res = editingLead
        ? await leadsService.updateLead(editingLead.id, leadForm)
        : await leadsService.createLead(leadForm);

      if (!res.success) {
        if (res.error === 'existing_customer') {
          const via = res.field === 'email' ? 'email address' : 'phone number';
          setFormError(`This ${via} is already registered as a customer${res.customer_name ? ` (${res.customer_name})` : ''}.`);
        } else if (res.error === 'existing_lead') {
          const via = res.field === 'email' ? 'email address' : 'phone number';
          setFormError(`An active lead already exists for this ${via}${res.lead_name ? ` — ${res.lead_name} (${res.lead_status})` : ''}.`);
        } else {
          setFormError(res.error || (editingLead ? 'Failed to update lead.' : 'Failed to create lead.'));
        }
        return;
      }

      showSuccess(editingLead ? 'Lead updated successfully.' : 'Lead created successfully.');
      setShowFormModal(false);
      fetchLeads();
    } catch (err) {
      setFormError(err.message || 'An error occurred.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Detail modal handlers ────────────────────────────────
  const handleViewDetail = async (lead) => {
    setDetailLead(lead);
    setShowDetailModal(true);
    setShowInteractionForm(false);
    setInteractionForm(BLANK_INTERACTION_FORM);
    setInteractionError('');
    setPendingStatus('');
    setStatusNote('');
    setLostReason('');
    setDetailLoading(true);
    try {
      const [leadRes, intRes] = await Promise.all([
        leadsService.getLead(lead.id),
        leadsService.getInteractions(lead.id),
      ]);
      setDetailLead(leadRes.data);
      setDetailInteractions(Array.isArray(intRes.data) ? intRes.data : []);
    } catch (err) {
      setError(err.message || 'Failed to load lead details');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    try {
      const [leadRes, intRes] = await Promise.all([
        leadsService.getLead(id),
        leadsService.getInteractions(id),
      ]);
      setDetailLead(leadRes.data);
      setDetailInteractions(Array.isArray(intRes.data) ? intRes.data : []);
      setLeads(prev => prev.map(l => l.id === id ? { ...l, ...leadRes.data } : l));
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Interaction handlers ─────────────────────────────────
  const handleInteractionChange = (e) => {
    const { name, value } = e.target;
    setInteractionForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLogInteraction = async (e) => {
    e.preventDefault();
    if (!interactionForm.interaction_type) {
      setInteractionError('Interaction type is required.');
      return;
    }
    setInteractionSubmitting(true);
    setInteractionError('');
    try {
      await leadsService.logInteraction(detailLead.id, interactionForm);
      setInteractionForm(BLANK_INTERACTION_FORM);
      setShowInteractionForm(false);
      showSuccess('Interaction logged.');
      await refreshDetail(detailLead.id);
    } catch (err) {
      setInteractionError(err.message || 'Failed to log interaction');
    } finally {
      setInteractionSubmitting(false);
    }
  };

  // ── Status change handlers ───────────────────────────────
  const handleStatusChange = async () => {
    if (!pendingStatus) return;
    setStatusSubmitting(true);
    setStatusError('');
    try {
      await leadsService.updateStatus(detailLead.id, pendingStatus, statusNote, lostReason);
      setPendingStatus('');
      setStatusNote('');
      setLostReason('');
      showSuccess(`Status changed to ${pendingStatus}.`);
      await refreshDetail(detailLead.id);
    } catch (err) {
      setStatusError(err.message || 'Failed to update status');
    } finally {
      setStatusSubmitting(false);
    }
  };

  // ── Resend Payment handler ───────────────────────────────
  const handleResendOpen = () => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 2);
    setResendDeliveryDate(defaultDate.toISOString().split('T')[0]);
    setResendDeliveryWindow('18:30 - 21:00');
    setResendError('');
    setShowResendModal(true);
  };

  const handleResendSubmit = async () => {
    setResendSubmitting(true);
    setResendError('');
    try {
      const res = await leadsService.resendPayment(detailLead.id, resendDeliveryDate, resendDeliveryWindow);
      setShowResendModal(false);
      showSuccess(`Payment link resent for order #${res.data.order_no}`);
      await refreshDetail(detailLead.id);
    } catch (err) {
      setResendError(err.message || 'Failed to resend payment link');
    } finally {
      setResendSubmitting(false);
    }
  };

  // ── Convert handlers ─────────────────────────────────────
  const searchTimers = useRef({});

  const handleConvertOpen = () => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 2);
    const defaultDateStr = defaultDate.toISOString().split('T')[0];

    setConvertAddress(detailLead?.area || '');
    setConvertDeliveryDate(defaultDateStr);
    setConvertDeliveryWindow('18:30 - 21:00');
    setConvertLines(blankLines());
    setConvertError('');
    setConvertTempPassword('');
    setConvertOrderNo('');
    setShowConvertModal(true);
  };

  const updateLine = (i, patch) =>
    setConvertLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const handleLineSearch = (i, value) => {
    updateLine(i, { search: value, product_id: null, product_name: '', unit_price: '', total_price: 0, suggestions: [], showSuggestions: false });
    clearTimeout(searchTimers.current[i]);
    if (value.length < 1) return;
    searchTimers.current[i] = setTimeout(async () => {
      try {
        const results = await leadsService.searchMenuProducts(value);
        updateLine(i, { suggestions: results, showSuggestions: results.length > 0 });
      } catch { /* silent */ }
    }, 250);
  };

  const handleLineSelect = (i, product) => {
    const qty = convertLines[i].qty || 1;
    const price = parseFloat(product.price || 0);
    updateLine(i, {
      product_id: product.id,
      product_name: product.name,
      search: product.name,
      unit_price: price,
      total_price: +(qty * price).toFixed(2),
      suggestions: [],
      showSuggestions: false,
      is_kit: !!product.is_kit,
      kit_item_days: product.kit_item_days || null,
      kit_schedules: product.kit_schedules || [],
      kit_schedule_id: (product.kit_schedules && product.kit_schedules[0]) ? product.kit_schedules[0].id : null,
      kit_consumption_days: product.kit_consumption_days || null,
    });
  };

  const handleLineQty = (i, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    const price = parseFloat(convertLines[i].unit_price || 0);
    updateLine(i, { qty, total_price: +(qty * price).toFixed(2) });
  };

  const convertTotal = convertLines.reduce((s, l) => s + parseFloat(l.total_price || 0), 0);

  const handleConvertSubmit = async () => {
    setConvertSubmitting(true);
    setConvertError('');
    try {
      const filledLines = convertLines
        .filter(l => l.product_id && Number(l.qty) > 0)
        .map(l => ({ product_id: l.product_id, qty: l.qty, unit_price: l.unit_price, total_price: l.total_price, kit_schedule_id: l.kit_schedule_id || null }));
      const res = await leadsService.convert(detailLead.id, {
        address: convertAddress,
        delivery_date: convertDeliveryDate || null,
        delivery_window: convertDeliveryWindow || null,
        lines: filledLines,
      });
      setConvertTempPassword(res.data.temp_password || '');
      setConvertOrderNo(res.data.order_no || '');
      fetchLeads();
    } catch (err) {
      setConvertError(err.message || 'Conversion failed');
    } finally {
      setConvertSubmitting(false);
    }
  };

  const handleConvertClose = () => {
    setShowConvertModal(false);
    setConvertTempPassword('');
    setConvertOrderNo('');
    setConvertError('');
    if (convertTempPassword) setShowDetailModal(false);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({ status: '', source_channel: '', assigned_to: '', action_filter: '' });
  };

  // ── Assign handlers ──────────────────────────────────────
  const handleAssignOpen = async (lead) => {
    setAssigningLead(lead);
    setSelectedAssignee(lead.assigned_to ? String(lead.assigned_to) : '');
    setAssignError('');
    setShowAssignModal(true);
    if (assignableUsers.length === 0) {
      try {
        const res = await leadsService.getUsersWithLeadsRead();
        setAssignableUsers(res.data || []);
      } catch (err) {
        setAssignError('Failed to load users: ' + err.message);
      }
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedAssignee) { setAssignError('Please select a user.'); return; }
    setAssignSubmitting(true);
    setAssignError('');
    try {
      await leadsService.assignLead(assigningLead.id, parseInt(selectedAssignee, 10));
      showSuccess('Lead assigned successfully.');
      setShowAssignModal(false);
      fetchLeads();
    } catch (err) {
      setAssignError(err.message || 'Failed to assign lead.');
    } finally {
      setAssignSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="leads-management">
      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Leads</h2>
          <p>Track prospects from first contact to customer conversion</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Link to="/leads/dashboard" className="btn-cancel" style={{ textDecoration: 'none', padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
            📊 Dashboard
          </Link>
          {hasImportPermission && (
            <Link to="/leads/import" className="btn-cancel" style={{ textDecoration: 'none', padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
              ↑ Import from Meta
            </Link>
          )}
          {hasImportPermission && (
            <Link to="/leads/import-aad" className="btn-cancel" style={{ textDecoration: 'none', padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
              ↑ Import Apple A Day
            </Link>
          )}
          {hasCreatePermission && (
            <button className="btn-create" onClick={handleCreateNew}>
              + New Lead
            </button>
          )}
        </div>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Search & Filters */}
      <div className="search-filter-container">
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filters-row">
          <select
            className="filter-select"
            name="status"
            value={filters.status}
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="filter-select"
            name="source_channel"
            value={filters.source_channel}
            onChange={e => setFilters(prev => ({ ...prev, source_channel: e.target.value }))}
          >
            <option value="">All Channels</option>
            {SOURCE_CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            className="filter-select"
            name="assigned_to"
            value={filters.assigned_to}
            onChange={e => setFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
          >
            <option value="">All Assignees</option>
            {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select
            className="filter-select"
            name="action_filter"
            value={filters.action_filter}
            onChange={e => setFilters(prev => ({ ...prev, action_filter: e.target.value }))}
          >
            <option value="">All Actions</option>
            <option value="overdue">Past Actions</option>
            <option value="due_today">Due Today</option>
            <option value="no_date">No Action Date</option>
          </select>
          <button className="btn-reset" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Area</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Interactions</th>
                <th>Next Action</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan={10} className="no-data">No leads found.</td></tr>
              ) : (
                leads.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(lead => (
                  <tr key={lead.id} className={lead.pincode_supported === false ? 'lead-row--unsupported' : ''}>
                    <td><strong>{lead.name}</strong></td>
                    <td>{lead.phone}</td>
                    <td>{lead.area || '—'}</td>
                    <td>{lead.source_channel || '—'}</td>
                    <td>
                      <span className={`state-badge ${STATUS_BADGE_CLASS[lead.status] || ''}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td>{lead.assigned_to_name || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{lead.interaction_count || 0}</td>
                    <td>
                      {lead.next_action_date
                        ? <span className={new Date(lead.next_action_date) < new Date() ? 'leads-overdue' : ''}>
                            {fmtDate(lead.next_action_date)}
                          </span>
                        : '—'}
                    </td>
                    <td>{fmtDate(lead.created_date)}</td>
                    <td>
                      <div className="action-buttons">
                        {hasUpdatePermission && lead.status !== 'Converted' && (
                          <button className="btn-icon btn-edit" title="Edit" onClick={() => handleEdit(lead)}>✏️</button>
                        )}
                        {hasUpdatePermission && (
                          <button className="btn-icon btn-assign" title="Assign" onClick={() => handleAssignOpen(lead)}>👤</button>
                        )}
                        <button className="btn-icon btn-view" title="View Details" onClick={() => handleViewDetail(lead)}>👁️</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {leads.length > PAGE_SIZE && (
          <div className="leads-pagination">
            <span className="leads-pagination-info">
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, leads.length)} of {leads.length}
            </span>
            <div className="leads-pagination-controls">
              <button
                className="leads-page-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >«</button>
              <button
                className="leads-page-btn"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >‹</button>
              <span className="leads-page-current">Page {currentPage} of {Math.ceil(leads.length / PAGE_SIZE)}</span>
              <button
                className="leads-page-btn"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === Math.ceil(leads.length / PAGE_SIZE)}
              >›</button>
              <button
                className="leads-page-btn"
                onClick={() => setCurrentPage(Math.ceil(leads.length / PAGE_SIZE))}
                disabled={currentPage === Math.ceil(leads.length / PAGE_SIZE)}
              >»</button>
            </div>
          </div>
        )}
        </>
      )}

      {/* ── Create / Edit Modal ──────────────────────────── */}
      {showFormModal && (
        <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
          <div className="modal-content leads-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingLead ? 'Edit Lead' : 'New Lead'}</h3>
              <button className="close-button" onClick={() => setShowFormModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleFormSubmit}>
                {formError && <div className="error-message">{formError}</div>}
                <div className="form-grid leads-form-grid">
                  <div className="form-group">
                    <label>Name <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input name="name" value={leadForm.name} onChange={handleFormChange} placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label>Phone <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input name="phone" value={leadForm.phone} onChange={handleFormChange} placeholder="10-digit mobile" />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input name="email" value={leadForm.email} onChange={handleFormChange} placeholder="Email address" />
                  </div>
                  <div className="form-group">
                    <label>Source Channel</label>
                    <select name="source_channel" value={leadForm.source_channel} onChange={handleFormChange}>
                      <option value="">— Select —</option>
                      {SOURCE_CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Source Reference</label>
                    <input name="source_ref" value={leadForm.source_ref} onChange={handleFormChange} placeholder="Referrer name, campaign, post URL..." />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Area / Address / Pincode</label>
                    <textarea name="area" value={leadForm.area} onChange={handleFormChange} placeholder="Locality, pincode, or full address" rows={3} style={{ height: 'auto' }} />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Notes</label>
                    <textarea name="notes" value={leadForm.notes} onChange={handleFormChange} rows={3} placeholder="Any additional notes..." style={{ height: 'auto' }} />
                  </div>
                </div>
              </form>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowFormModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleFormSubmit} disabled={formSubmitting}>
                {formSubmitting ? 'Saving...' : editingLead ? 'Save Changes' : 'Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail Modal ──────────────────────────────────── */}
      {showDetailModal && detailLead && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content leads-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{detailLead.name}</h3>
                <span className={`state-badge ${STATUS_BADGE_CLASS[detailLead.status] || ''}`}>
                  {detailLead.status}
                </span>
              </div>
              <button className="close-button" onClick={() => setShowDetailModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {detailLoading ? (
                <div className="loading">Loading...</div>
              ) : (
                <>
                  {/* Lead Info */}
                  <div className="leads-detail-info">
                    <div className="leads-info-grid">
                      <div className="leads-info-item">
                        <span className="leads-info-label">Phone</span>
                        <span>{detailLead.phone}</span>
                      </div>
                      {detailLead.email && (
                        <div className="leads-info-item">
                          <span className="leads-info-label">Email</span>
                          <span>{detailLead.email}</span>
                        </div>
                      )}
                      {detailLead.area && (
                        <div className="leads-info-item">
                          <span className="leads-info-label">Area</span>
                          <span>{detailLead.area}</span>
                        </div>
                      )}
                      {detailLead.source_channel && (
                        <div className="leads-info-item">
                          <span className="leads-info-label">Source</span>
                          <span>{detailLead.source_channel}{detailLead.source_ref ? ` — ${detailLead.source_ref}` : ''}</span>
                        </div>
                      )}
                      {detailLead.notes && (
                        <div className="leads-info-item leads-info-item--full">
                          <span className="leads-info-label">Notes</span>
                          <span>{detailLead.notes}</span>
                        </div>
                      )}
                      {detailLead.converted_customer_id && (
                        <div className="leads-info-item">
                          <span className="leads-info-label">Customer #</span>
                          <span>{detailLead.converted_customer_id}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Converted: show order_lc_state and Order Placed transition */}
                  {detailLead.status === 'Converted' && detailLead.converted_customer_id && hasUpdatePermission && (
                    <div className="leads-status-section">
                      <div className="section-title-row">
                        <span className="section-title">Subscription Journey</span>
                      </div>
                      <div className="leads-status-actions">
                        <button
                          className={`leads-status-btn leads-status-btn--order-placed ${pendingStatus === 'Order Placed' ? 'leads-status-btn--active' : ''}`}
                          disabled={detailLead.order_lc_state !== 'Placed'}
                          title={detailLead.order_lc_state !== 'Placed' ? 'Order must be in Placed state to proceed' : ''}
                          onClick={() => setPendingStatus(pendingStatus === 'Order Placed' ? '' : 'Order Placed')}
                        >
                          → Order Placed
                        </button>
                        {(detailLead.customer_orders || []).some(o => o.order_lc_state === 'Payment Requested') && (
                          <button
                            className="leads-status-btn leads-status-btn--resend-payment"
                            onClick={handleResendOpen}
                          >
                            ↺ Resend Payment Link
                          </button>
                        )}
                      </div>
                      {pendingStatus === 'Order Placed' && (
                        <>
                          <div className="leads-status-confirm">
                            <input
                              className="leads-status-input"
                              placeholder="Notes (optional)"
                              value={statusNote}
                              onChange={e => setStatusNote(e.target.value)}
                            />
                            <button className="btn-submit leads-status-confirm-btn" onClick={handleStatusChange} disabled={statusSubmitting}>
                              {statusSubmitting ? 'Saving...' : 'Confirm → Order Placed'}
                            </button>
                            <button className="btn-cancel" onClick={() => { setPendingStatus(''); setStatusError(''); }} style={{ padding: '0.4rem 0.8rem' }}>Cancel</button>
                          </div>
                          {statusError && <div className="leads-status-error">{statusError}</div>}
                        </>
                      )}
                    </div>
                  )}

                  {/* Status Transitions (non-Converted statuses) */}
                  {detailLead.status !== 'Converted' && (NEXT_STATUS[detailLead.status] || []).length > 0 && (
                    <div className="leads-status-section">
                      <div className="section-title-row">
                        <span className="section-title">Move Status</span>
                      </div>
                      <div className="leads-status-actions">
                        {(NEXT_STATUS[detailLead.status] || []).map(s => {
                          const hasPywPlaced = (detailLead.customer_orders || []).some(
                            o => o.plan_your_week && o.order_lc_state === 'Placed'
                          );
                          const isDisabled = s === 'Subscribed' && !hasPywPlaced;
                          return (
                            <button
                              key={s}
                              className={`leads-status-btn leads-status-btn--${s.toLowerCase().replace(/\s+/g, '-')} ${pendingStatus === s ? 'leads-status-btn--active' : ''}`}
                              disabled={isDisabled}
                              title={isDisabled ? 'Requires at least one PYW order in Placed state' : ''}
                              onClick={() => setPendingStatus(pendingStatus === s ? '' : s)}
                            >
                              {s === 'Lost' || s === 'Lost on Subscription' ? `✕ ${s}` : `→ ${s}`}
                            </button>
                          );
                        })}
                      </div>
                      {pendingStatus && (
                        <>
                          <div className="leads-status-confirm">
                            {pendingStatus === 'Lost' && (
                              <input
                                className="leads-status-input"
                                placeholder="Reason for loss (optional)"
                                value={lostReason}
                                onChange={e => setLostReason(e.target.value)}
                              />
                            )}
                            <input
                              className="leads-status-input"
                              placeholder="Notes (optional)"
                              value={statusNote}
                              onChange={e => setStatusNote(e.target.value)}
                            />
                            <button className="btn-submit leads-status-confirm-btn" onClick={handleStatusChange} disabled={statusSubmitting}>
                              {statusSubmitting ? 'Saving...' : `Confirm → ${pendingStatus}`}
                            </button>
                            <button className="btn-cancel" onClick={() => { setPendingStatus(''); setStatusError(''); }} style={{ padding: '0.4rem 0.8rem' }}>Cancel</button>
                          </div>
                          {statusError && <div className="leads-status-error">{statusError}</div>}
                        </>
                      )}
                    </div>
                  )}

                  {/* Orders table — visible for all post-conversion statuses */}
                  {detailLead.converted_customer_id && (
                    <div className="leads-orders-section">
                      <div className="section-title-row">
                        <span className="section-title">Orders</span>
                      </div>
                      {(detailLead.customer_orders || []).length === 0 ? (
                        <div className="leads-orders-empty">No orders placed yet</div>
                      ) : (
                        <table className="leads-orders-table">
                          <thead>
                            <tr>
                              <th>Order No</th>
                              <th>Order Date</th>
                              <th>Delivery Date</th>
                              <th>Window</th>
                              <th>Type</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detailLead.customer_orders || []).map(o => (
                              <tr key={o.order_no}>
                                <td>#{o.order_no}</td>
                                <td>{fmtDate(o.order_date)}</td>
                                <td>{fmtDate(o.delivery_date)}</td>
                                <td>{o.delivery_window || '—'}</td>
                                <td>{o.plan_your_week ? <strong>PYW</strong> : 'Regular'}</td>
                                <td>
                                  <span className={`state-badge ${ORDER_STATE_BADGE[o.order_lc_state] || 'status-new'}`}>
                                    {o.order_lc_state}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Convert to Customer */}
                  {detailLead.status === 'Interested' && hasConvertPermission && (
                    <div className="leads-convert-banner">
                      <span>This lead is ready to convert.</span>
                      <button className="leads-convert-btn" onClick={handleConvertOpen}>
                        Convert to Customer →
                      </button>
                    </div>
                  )}

                  {/* Interaction Log */}
                  <div className="leads-interactions-section">
                    <div className="section-title-row">
                      <span className="section-title">Interaction History</span>
                      {detailLead.status !== 'Converted' && (
                        <button className="btn-add-line" onClick={() => setShowInteractionForm(v => !v)}>
                          {showInteractionForm ? 'Cancel' : '+ Log Interaction'}
                        </button>
                      )}
                    </div>

                    {/* New Interaction Form */}
                    {showInteractionForm && (
                      <div className="leads-interaction-form">
                        {interactionError && <div className="error-message">{interactionError}</div>}
                        <div className="form-grid leads-interaction-grid">
                          <div className="form-group">
                            <label>Type <span style={{ color: '#e74c3c' }}>*</span></label>
                            <select name="interaction_type" value={interactionForm.interaction_type} onChange={handleInteractionChange}>
                              <option value="">— Select —</option>
                              {INTERACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Direction</label>
                            <select name="direction" value={interactionForm.direction} onChange={handleInteractionChange}>
                              <option value="Outbound">Outbound</option>
                              <option value="Inbound">Inbound</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Channel</label>
                            <select name="channel" value={interactionForm.channel} onChange={handleInteractionChange}>
                              <option value="">— Select —</option>
                              <option value="WhatsApp">WhatsApp</option>
                              <option value="Phone">Phone</option>
                              <option value="In-person">In-person</option>
                              <option value="Email">Email</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Outcome</label>
                            <select name="outcome" value={interactionForm.outcome} onChange={handleInteractionChange}>
                              <option value="">— Select —</option>
                              {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div className="form-group form-group-full">
                            <label>Summary</label>
                            <textarea name="summary" value={interactionForm.summary} onChange={handleInteractionChange} rows={2} placeholder="What was discussed..." style={{ height: 'auto' }} />
                          </div>
                          <div className="form-group">
                            <label>Next Action</label>
                            <input name="next_action" value={interactionForm.next_action} onChange={handleInteractionChange} placeholder="e.g. Send price list" />
                          </div>
                          <div className="form-group">
                            <label>Follow-up Date</label>
                            <input type="date" name="next_action_date" value={interactionForm.next_action_date} onChange={handleInteractionChange} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <button className="btn-cancel" onClick={() => setShowInteractionForm(false)}>Cancel</button>
                          <button className="btn-submit" onClick={handleLogInteraction} disabled={interactionSubmitting}>
                            {interactionSubmitting ? 'Saving...' : 'Save Interaction'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Interaction Table */}
                    {detailInteractions.length === 0 ? (
                      <p className="no-data">No interactions logged yet.</p>
                    ) : (
                      <table className="orders-table leads-interaction-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Direction</th>
                            <th>Summary</th>
                            <th>Outcome</th>
                            <th>Next Action</th>
                            <th>Follow-up</th>
                            <th>By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailInteractions.map(i => (
                            <tr key={i.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(i.interaction_date)}</td>
                              <td>{i.interaction_type}</td>
                              <td>{i.direction || '—'}</td>
                              <td>{i.summary || '—'}</td>
                              <td>{i.outcome || '—'}</td>
                              <td>{i.next_action || '—'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{i.next_action_date ? fmtDate(i.next_action_date) : '—'}</td>
                              <td>{i.interacted_by_name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Status History */}
                  {detailLead.status_history && detailLead.status_history.length > 0 && (
                    <div className="leads-status-history">
                      <div className="section-title-row" style={{ marginTop: '1rem' }}>
                        <span className="section-title">Status History</span>
                      </div>
                      <div className="leads-history-trail">
                        {detailLead.status_history.map(h => (
                          <div key={h.id} className="leads-history-item">
                            <span className={`state-badge ${STATUS_BADGE_CLASS[h.to_status] || ''}`}>{h.to_status}</span>
                            <span className="leads-history-meta">
                              {h.from_status ? `from ${h.from_status} · ` : ''}
                              {fmtDate(h.changed_date)}
                              {h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
                              {h.notes ? ` — ${h.notes}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ──────────────────────────────────── */}
      {showAssignModal && assigningLead && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content leads-form-modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Lead — {assigningLead.name}</h3>
              <button className="close-button" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {assignError && <div className="error-message">{assignError}</div>}
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={selectedAssignee}
                  onChange={e => setSelectedAssignee(e.target.value)}
                >
                  <option value="">— Select user —</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleAssignSubmit} disabled={assignSubmitting}>
                {assignSubmitting ? 'Saving...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Convert Modal ──────────────────────────────────── */}
      {showConvertModal && (
        <div className="modal-overlay">
          <div className="modal-content leads-convert-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Convert to Customer — {detailLead?.name}</h3>
              <button className="close-button" onClick={handleConvertClose}>×</button>
            </div>

            {convertTempPassword ? (
              /* ── Success state ── */
              <>
                <div className="modal-body">
                  <div ref={convertMsgRef} className="convert-success-banner">
                    <div className="convert-success-title">Customer account created!</div>
                    <p>A login has been set up for <strong>{detailLead?.name}</strong>. Share these credentials:</p>
                    <div className="convert-credentials">
                      <div><span className="cred-label">Username / Phone</span><span className="cred-value">{detailLead?.phone}</span></div>
                      <div><span className="cred-label">Temporary Password</span><span className="cred-value cred-password">{convertTempPassword}</span></div>
                    </div>
                    {convertOrderNo && (
                      <p style={{ marginTop: '0.75rem', color: '#2c3e50', fontSize: '0.88rem' }}>
                        First order <strong>{convertOrderNo}</strong> created successfully.
                      </p>
                    )}
                    <p className="convert-whatsapp-note">A WhatsApp welcome message has been sent to the customer.</p>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="btn-submit" onClick={handleConvertClose}>Done</button>
                </div>
              </>
            ) : (
              /* ── Form state ── */
              <>
                <div className="modal-body">
                  {convertError && <div ref={convertMsgRef} className="error-message">{convertError}</div>}

                  {/* Address — max-width constrained */}
                  <div className="convert-section">
                    <div className="convert-section-title">Delivery Address</div>
                    <div className="convert-address-wrap">
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <textarea
                          value={convertAddress}
                          onChange={e => setConvertAddress(e.target.value)}
                          rows={3}
                          placeholder="e.g. 12 Main Street, Koramangala, Bangalore 560034"
                          style={{ height: 'auto' }}
                        />
                        {detailLead?.source_channel !== 'Apple A Day' && (
                          <span className="convert-pincode-hint">Must include a 6-digit serviceable pincode</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Optional delivery info */}
                  <div className="convert-section">
                    <div className="convert-section-title">First Delivery <span className="convert-optional">(optional)</span></div>
                    <div className="convert-delivery-row">
                      <div className="form-group">
                        <label>Delivery Date</label>
                        <input type="date" value={convertDeliveryDate} onChange={e => setConvertDeliveryDate(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Time Window</label>
                        <select value={convertDeliveryWindow} onChange={e => setConvertDeliveryWindow(e.target.value)}>
                          <option value="">— select —</option>
                          {DELIVERY_WINDOWS.map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Product lines */}
                  <div className="convert-section">
                    <div className="convert-section-title">Initial Order <span className="convert-optional">(optional)</span></div>
                    <table className="convert-lines-table">
                      <thead>
                        <tr>
                          <th style={{ width: '2rem' }}>#</th>
                          <th>Product</th>
                          <th style={{ width: '5rem' }}>Qty</th>
                          <th style={{ width: '6rem' }}>Price</th>
                          <th style={{ width: '7rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {convertLines.map((line, i) => {
                          const id = line.kit_item_days || 0;
                          return (
                            <Fragment key={i}>
                              <tr>
                                <td className="convert-line-num">{i + 1}</td>
                                <td className="convert-line-product">
                                  <div className="convert-product-wrap">
                                    <input
                                      type="text"
                                      value={line.search}
                                      onChange={e => handleLineSearch(i, e.target.value)}
                                      onBlur={() => setTimeout(() => updateLine(i, { showSuggestions: false }), 200)}
                                      onFocus={() => line.suggestions.length > 0 && updateLine(i, { showSuggestions: true })}
                                      placeholder="Search product…"
                                      autoComplete="off"
                                    />
                                    {line.showSuggestions && (
                                      <ul className="convert-product-dropdown">
                                        {line.suggestions.map(p => (
                                          <li key={p.id} onMouseDown={() => handleLineSelect(i, p)}>
                                            {p.name}
                                            {p.is_kit && <span className="convert-kit-badge">Kit</span>}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="1"
                                    value={line.qty}
                                    onChange={e => handleLineQty(i, e.target.value)}
                                    className="convert-qty-input"
                                    disabled={!line.product_id}
                                  />
                                </td>
                                <td className="convert-line-price">
                                  {line.unit_price !== '' ? `₹${parseFloat(line.unit_price).toFixed(2)}` : '—'}
                                </td>
                                <td className="convert-line-total">
                                  {line.total_price > 0 ? `₹${parseFloat(line.total_price).toFixed(2)}` : '—'}
                                </td>
                              </tr>
                              {line.is_kit && line.product_id && (
                                <tr className="convert-kit-info-row">
                                  <td />
                                  <td colSpan={4}>
                                    <span className="convert-kit-badge">Kit</span>
                                    {id > 0 && <span>{id}-day supply</span>}
                                    {(line.kit_schedules || []).length > 0 && (
                                      <div className="convert-kit-schedules">
                                        <span className="convert-days-label">Select Delivery Schedule:</span>
                                        {(line.kit_schedules || []).map((sched) => {
                                          const selected = line.kit_schedule_id === sched.id;
                                          return (
                                            <div
                                              key={sched.id}
                                              className={`convert-kit-schedule-row${selected ? ' convert-kit-schedule-row--selected' : ''}`}
                                              onClick={() => updateLine(i, { kit_schedule_id: selected ? null : sched.id })}
                                            >
                                              <span className="convert-schedule-name">{sched.schedule_name}</span>
                                              <div className="convert-days-display">
                                                {DOW_SHORT.map((day, idx) => (
                                                  <span
                                                    key={idx}
                                                    className={`convert-day-chip${(sched.delivery_days || []).includes(idx) ? ' convert-day-chip--active' : ''}`}
                                                  >
                                                    {day}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                      {convertTotal > 0 && (
                        <tfoot>
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600, paddingRight: '0.5rem' }}>Order Total</td>
                            <td className="convert-line-total" style={{ fontWeight: 700 }}>₹{convertTotal.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn-cancel" onClick={handleConvertClose}>Cancel</button>
                  <button className="btn-submit" onClick={handleConvertSubmit} disabled={convertSubmitting}>
                    {convertSubmitting ? 'Converting...' : 'Confirm Conversion'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Resend Payment Modal */}
      {showResendModal && detailLead && (
        <div className="modal-overlay" onClick={() => setShowResendModal(false)}>
          <div className="modal-content leads-resend-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Resend Payment Link — {detailLead.name}</h3>
              <button className="close-button" onClick={() => setShowResendModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {resendError && <div className="error-message">{resendError}</div>}
              <div className="convert-section">
                <div className="convert-section-title">Delivery Details</div>
                <div className="convert-delivery-row">
                  <div className="convert-delivery-field">
                    <label>Delivery Date</label>
                    <input
                      type="date"
                      value={resendDeliveryDate}
                      onChange={e => setResendDeliveryDate(e.target.value)}
                    />
                  </div>
                  <div className="convert-delivery-field">
                    <label>Delivery Window</label>
                    <select
                      value={resendDeliveryWindow}
                      onChange={e => setResendDeliveryWindow(e.target.value)}
                    >
                      <option value="">— select —</option>
                      {DELIVERY_WINDOWS.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <p className="convert-whatsapp-note">A new payment link valid for 48 hours will be sent via WhatsApp.</p>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowResendModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleResendSubmit} disabled={resendSubmitting}>
                {resendSubmitting ? 'Sending...' : 'Send Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
